import type { ComplianceStreamEvent } from "@amoreg/contracts";
import { sameReviewQueueCounts, type ReviewCounts } from "@repo/shared/reviewCounts";

export interface ReviewCountUpdate {
  readonly counts: ReviewCounts;
  readonly event: ComplianceStreamEvent | null;
}

const NONE = { total: 0, open: 0, accepted: 0 } as const;

/** Housekeeping suggestions alone: every suggestion minus the gaps. */
const recordUpdates = (counts: ReviewCounts) => {
  const gaps = counts.gaps ?? NONE;
  return {
    total: counts.suggestions.total - gaps.total,
    open: counts.suggestions.open - gaps.open,
    accepted: counts.suggestions.accepted - gaps.accepted,
  };
};

/**
 * Initial reads, reconnect snapshots and repeated revisions are silent. Gap
 * suggestions (control amendments, missing documents) are findings, so a change
 * to them reads "Findings updated"; the rest are record updates.
 */
export function reviewCountNotification(previous: ReviewCountUpdate | null, next: ReviewCountUpdate): string | null {
  if (!previous || next.event?.type !== "changed" ||
    (previous.event !== null && previous.event.organizationId !== next.event.organizationId) ||
    previous.event?.revision === next.event.revision) return null;
  const recordsChanged = !sameReviewQueueCounts(recordUpdates(previous.counts), recordUpdates(next.counts));
  const findingsChanged =
    !sameReviewQueueCounts(previous.counts.findings, next.counts.findings) ||
    !sameReviewQueueCounts(previous.counts.gaps ?? NONE, next.counts.gaps ?? NONE);
  return recordsChanged && findingsChanged ? "Findings and suggestions changed"
    : recordsChanged ? "Suggestions changed"
    : findingsChanged ? "Findings updated" : null;
}

/**
 * When this tab last changed a finding or a record update itself. The page
 * that made the change already says what happened, so the live count push
 * that follows must not cover that toast with a generic one.
 */
let lastLocalWrite = 0;
export const LOCAL_WRITE_QUIET_MS = 15_000;
export const noteLocalReviewWrite = (now: number = Date.now()) => {
  lastLocalWrite = now;
};
export const recentLocalReviewWrite = (now: number = Date.now()) => now - lastLocalWrite < LOCAL_WRITE_QUIET_MS;
